import { useAuth } from "@crm/access/hooks/use-auth";
import { useAtomValue } from "@effect/atom-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Option } from "effect";
import { AsyncResult } from "effect/unstable/reactivity";
import { ArrowRight, BookOpen, Check, Clock, Globe, Library, Megaphone, Play } from "lucide-react";
import { useMemo } from "react";
import { useKnowledgeEntriesInfinite } from "#app/knowledge/knowledge.queries";
import { useReviewCourses } from "#app/review-courses";
import { useMyCapabilities } from "#app/sites";
import { studentTaskContextAtom } from "#app/student-tasks/queries";
import { useCoursesProgress } from "./review-course/-hooks/use-courses-progress";

/**
 * Workbench 首页（redesign v2）：以"来上线下课"为主轴。
 * 班级 Hero → 待办作业 → 配套复习（bonus）→ 知识库更新 → 工具。
 * 数据全部来自真实 handler，学员/员工双形态。
 */
export const Route = createFileRoute("/_protected/")({
  head: () => ({ meta: [{ title: "首页 · imiker 学员工作台" }] }),
  component: HomePage,
});

const TASK_TYPE_LABELS = {
  in_class: { label: "课堂任务", className: "bg-brand-soft text-brand-text" },
  evening: { label: "晚间作业", className: "bg-warning-soft text-warning" },
  mikequan: { label: "米课圈", className: "bg-violet-soft text-violet" },
} as const;

function greetingByHour(): string {
  const h = Number(
    new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      hour12: false,
    })
      .formatToParts(Date.now())
      .find((part) => part.type === "hour")?.value ?? 0,
  );
  if (h < 6) {
    return "夜深了";
  }
  if (h < 12) {
    return "早上好";
  }
  if (h < 14) {
    return "中午好";
  }
  if (h < 18) {
    return "下午好";
  }
  return "晚上好";
}

function formatDeadline(ms: number | null): {
  text: string;
  tone: "hot" | "warm" | "normal";
} {
  if (!ms) {
    return { text: "无截止时间", tone: "normal" };
  }
  const diff = ms - Date.now();
  const hours = Math.round(diff / 3_600_000);
  if (diff < 0) {
    return { text: "已逾期", tone: "hot" };
  }
  if (hours < 24) {
    return { text: `${hours} 小时内截止`, tone: "hot" };
  }
  if (hours < 48) {
    return { text: "明天截止", tone: "warm" };
  }
  return { text: `${Math.round(hours / 24)} 天后截止`, tone: "normal" };
}

function formatSessionDate(ms: number | null): string {
  if (!ms) {
    return "";
  }
  return new Intl.DateTimeFormat("zh-CN", {
    month: "long",
    day: "numeric",
  }).format(ms);
}

function formatWireDate(value: Date | string | null): string {
  if (!value) {
    return "";
  }
  const epochMs = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(epochMs) ? new Intl.DateTimeFormat("zh-CN").format(epochMs) : "";
}

function HomePage() {
  const { user } = useAuth();
  const isStudent = user?.role === "student";
  const { capabilities } = useMyCapabilities(user?.id);

  const taskContextResult = useAtomValue(
    studentTaskContextAtom([user?.studentId ?? ""], isStudent && !!user?.studentId),
  );
  const taskContext = AsyncResult.value(taskContextResult).pipe(Option.getOrUndefined);

  const classSessions = useMemo(() => taskContext?.classSessions ?? [], [taskContext]);
  const currentSession =
    classSessions.find((cs) => cs.enrollmentStatus === "in_class") ?? classSessions[0];

  const pendingTasks = useMemo(
    () =>
      classSessions
        .flatMap((cs) => cs.tasks)
        .filter((task) => task.canSubmit)
        .sort(
          (a, b) =>
            (a.deadline ?? Number.POSITIVE_INFINITY) - (b.deadline ?? Number.POSITIVE_INFINITY),
        )
        .slice(0, 3),
    [classSessions],
  );

  const coursesResult = useReviewCourses({ status: "published" });
  const courses = AsyncResult.value(coursesResult).pipe(Option.getOrUndefined) ?? [];
  const firstCourse = courses[0];
  const progressMap = useCoursesProgress(firstCourse ? [firstCourse.id] : []);
  const courseProgress = firstCourse ? progressMap.get(firstCourse.id) : undefined;

  const { items: latestKnowledge } = useKnowledgeEntriesInfinite({
    status: "published",
    pageSize: 2,
  });

  return (
    <div className="max-w-[1020px] px-9 pt-6 pb-16">
      {/* 问候 */}
      <div className="mb-6">
        <h1 className="font-display font-semibold text-[34px] tracking-tight">
          {greetingByHour()}，<em className="text-brand-text italic">{user?.name ?? "同学"}</em>
        </h1>
        <p className="mt-1.5 text-[13.5px] text-ink-2">
          {isStudent && currentSession ? (
            <>
              你的班级 <strong className="text-ink">{currentSession.classSessionName}</strong>
              {currentSession.groupName ? (
                <>
                  {" "}
                  · <strong className="text-ink">{currentSession.groupName}</strong>
                </>
              ) : null}
              {pendingTasks.length > 0 ? (
                <>
                  {" "}
                  · 还有 <strong className="text-ink">{pendingTasks.length} 项作业</strong>
                  待完成
                </>
              ) : (
                " · 作业都完成了，去看看复习视频吧"
              )}
            </>
          ) : (
            "欢迎使用 imiker 学员工作台"
          )}
        </p>
      </div>

      {/* 班级 Hero（仅学员） */}
      {isStudent && currentSession ? (
        <div className="relative overflow-hidden rounded-[20px] bg-gradient-to-br from-[#3730A3] via-brand to-[#6D5DF6] p-7 text-white shadow-[0_12px_32px_rgba(79,70,229,0.26)]">
          <div className="relative">
            <div className="flex items-center gap-2 font-semibold text-[11.5px] uppercase tracking-widest opacity-75">
              <span className="h-[7px] w-[7px] rounded-full bg-green-400 shadow-[0_0_0_3px_rgba(74,222,128,0.25)]" />
              我的班级 · {currentSession.enrollmentStatus === "in_class" ? "进行中" : "已结课"}
            </div>
            <div className="mt-2 font-display font-semibold text-[26px] tracking-tight">
              {currentSession.classSessionName}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {currentSession.groupName ? (
                <span className="rounded-full bg-white/15 px-3 py-1 font-medium text-[12px] backdrop-blur-sm">
                  {currentSession.groupName}
                </span>
              ) : null}
              {currentSession.classSessionStartDate ? (
                <span className="rounded-full bg-white/15 px-3 py-1 font-medium text-[12px] backdrop-blur-sm">
                  {formatSessionDate(currentSession.classSessionStartDate)} 开课
                  {currentSession.classSessionEndDate
                    ? ` · ${formatSessionDate(currentSession.classSessionEndDate)} 结课`
                    : ""}
                </span>
              ) : null}
            </div>
            {currentSession.groupId ? (
              <Link
                className="hover:-translate-y-px mt-5 inline-flex items-center gap-2 rounded-[11px] bg-white px-[18px] py-2.5 font-semibold text-[13.5px] text-brand-text shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-transform"
                params={{
                  classSessionId: currentSession.classSessionId,
                  groupId: currentSession.groupId,
                }}
                to="/class-progress/$classSessionId/$groupId"
              >
                查看课堂进度
                <ArrowRight size={15} />
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* 待办作业（仅学员） */}
      {isStudent && pendingTasks.length > 0 ? (
        <section className="mt-8">
          <div className="mb-3.5 flex items-baseline gap-2.5">
            <span className="font-bold text-base tracking-tight">待办作业</span>
            <span className="font-medium text-[12.5px] text-ink-3">
              {pendingTasks.length} 项 · 按截止时间排序
            </span>
            <Link
              className="ml-auto font-medium text-[12.5px] text-brand hover:underline"
              to="/student-tasks"
            >
              全部作业 →
            </Link>
          </div>
          <div className="flex flex-col gap-2.5">
            {pendingTasks.map((task) => {
              const typeInfo = TASK_TYPE_LABELS[task.type] ?? TASK_TYPE_LABELS.in_class;
              const dl = formatDeadline(task.deadline);
              return (
                <Link
                  className="hover:-translate-y-px flex items-center gap-3.5 rounded-[14px] border border-border bg-card px-[18px] py-[15px] transition-all hover:shadow-[0_2px_6px_rgba(28,25,23,0.05),0_10px_28px_rgba(28,25,23,0.08)]"
                  key={task.id}
                  to="/student-tasks"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 border-[#D6D2C9]">
                    <Check className="h-3 w-3 text-transparent" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">{task.title}</div>
                    {task.description ? (
                      <div className="mt-0.5 max-w-[460px] truncate text-[12.5px] text-ink-2">
                        {task.description}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <span
                      className={`rounded-full px-[9px] py-[3px] font-semibold text-[11px] ${typeInfo.className}`}
                    >
                      {typeInfo.label}
                    </span>
                    <span
                      className={`flex items-center gap-1 text-[12px] ${
                        dl.tone === "hot"
                          ? "font-semibold text-danger"
                          : dl.tone === "warm"
                            ? "text-warning"
                            : "text-ink-2"
                      }`}
                    >
                      <Clock size={13} />
                      {dl.text}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* 配套复习（bonus，仅学员） */}
      {isStudent && firstCourse ? (
        <section className="mt-8">
          <div className="mb-3.5 flex items-baseline gap-2.5">
            <span className="font-bold text-base tracking-tight">配套复习</span>
            <span className="font-medium text-[12.5px] text-ink-3">课堂内容随时回看</span>
          </div>
          <Link
            className="flex items-center gap-[18px] rounded-[14px] border border-[#EAE2D5] bg-[#F5F0E9] px-5 py-4 transition-shadow hover:shadow-[0_1px_2px_rgba(28,25,23,0.05),0_2px_8px_rgba(28,25,23,0.04)]"
            params={{ courseId: firstCourse.id }}
            to="/review-course/$courseId"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-card text-brand-text shadow-[0_1px_2px_rgba(28,25,23,0.05)]">
              <Play size={20} />
            </span>
            <div className="min-w-0">
              <div className="font-bold text-[10.5px] text-warning uppercase tracking-widest">
                Bonus
              </div>
              <div className="truncate font-semibold text-sm">{firstCourse.name}</div>
            </div>
            <div className="ml-auto w-full min-w-0 max-w-[300px]">
              <div className="h-[5px] overflow-hidden rounded-full bg-ink/8">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-brand to-violet"
                  style={{
                    width: `${courseProgress?.completionPercentage ?? 0}%`,
                  }}
                />
              </div>
              <div className="mt-1.5 flex justify-between text-[11.5px] text-ink-2">
                <span>{courseProgress?.totalLessons ?? 0} 节</span>
                <span>已看 {courseProgress?.completedLessons ?? 0} 节</span>
              </div>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-[10px] bg-ink px-4 py-[9px] font-semibold text-[12.5px] text-white">
              <Play size={13} />
              继续看
            </span>
          </Link>
        </section>
      ) : null}

      {/* 知识库更新 */}
      {latestKnowledge.length > 0 ? (
        <section className="mt-8">
          <div className="mb-3.5 flex items-baseline gap-2.5">
            <span className="font-bold text-base tracking-tight">知识库更新</span>
            <Link
              className="ml-auto font-medium text-[12.5px] text-brand hover:underline"
              to="/knowledge"
            >
              进入知识库 →
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {latestKnowledge.map((entry) => (
              <Link
                className="hover:-translate-y-px rounded-[14px] border border-border bg-card p-[18px_20px] transition-all hover:shadow-[0_2px_6px_rgba(28,25,23,0.05),0_10px_28px_rgba(28,25,23,0.08)]"
                key={entry.id}
                params={{ id: entry.id }}
                to="/knowledge/articles/$id"
              >
                <span className="mb-2.5 inline-block rounded-full bg-brand-soft px-[9px] py-[3px] font-semibold text-[11px] text-brand-text">
                  知识
                </span>
                <div className="font-semibold text-sm leading-snug">{entry.title}</div>
                <div className="mt-2 text-[12px] text-ink-3">{formatWireDate(entry.updatedAt)}</div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {/* 工具入口（课程能力门禁，与侧栏同一份声明） */}
      <section className="mt-8">
        <div className="mb-3.5 flex items-baseline gap-2.5">
          <span className="font-bold text-base tracking-tight">我的工具</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {capabilities.has("site-tools") ? (
            <ToolCard
              desc="我的站点 · ACF 模板 · 部署记录"
              icon={<Globe size={20} />}
              iconClass="bg-brand-soft text-brand-text"
              title="建站工具"
              to="/site-tools"
            />
          ) : null}
          {capabilities.has("sio-tools") ? (
            <ToolCard
              desc="关键词研究 · SIO 博客 · 外链分析"
              icon={<Megaphone size={20} />}
              iconClass="bg-violet-soft text-violet"
              title="推广工具"
              to="/sio-tools"
            />
          ) : null}
          <ToolCard
            desc="教练团队沉淀的实战方法论"
            icon={<Library size={20} />}
            iconClass="bg-success-soft text-success"
            title="知识库"
            to="/knowledge"
          />
          {capabilities.has("design-templates") ? (
            <ToolCard
              desc="整站设计实例，预览下载一键复刻"
              icon={<BookOpen size={20} />}
              iconClass="bg-warning-soft text-warning"
              title="设计参考"
              to="/site-tools/references"
            />
          ) : null}
        </div>
      </section>
    </div>
  );
}

function ToolCard({
  desc,
  icon,
  iconClass,
  title,
  to,
}: {
  desc: string;
  icon: React.ReactNode;
  iconClass: string;
  title: string;
  to: string;
}) {
  return (
    <Link
      className="hover:-translate-y-px flex items-center gap-3.5 rounded-[14px] border border-border bg-card p-[18px_20px] transition-all hover:shadow-[0_2px_6px_rgba(28,25,23,0.05),0_10px_28px_rgba(28,25,23,0.08)]"
      to={to}
    >
      <span
        className={`flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl ${iconClass}`}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <div className="font-semibold text-sm">{title}</div>
        <div className="mt-0.5 truncate text-[12px] text-ink-2">{desc}</div>
      </div>
      <ArrowRight className="ml-auto shrink-0 text-ink-3" size={16} />
    </Link>
  );
}
